import { useMemo, useState, useRef, useLayoutEffect, useCallback, useEffect } from "react";
import { Sparkles, Gem, Lock, Check, Star, Shield, Package, Plus, Minus, Scan } from "lucide-react";

const RARITY_COLORS = {
  common: "#9ca3af", uncommon: "#34d399", rare: "#38bdf8",
  epic: "#a78bfa", legendary: "#fbbf24", goddess: "#f472b6",
};
const TYPE_ICON = { card: Gem, gear: Shield, pack: Package, buff: Sparkles };
const TYPE_LABEL = { card: "Card", gear: "Gear", pack: "Pack", buff: "Buff" };

const COL_W = 150;
const ROW_H = 96;
const PAD_X = 70;
const PAD_Y = 64;
const MIN_SCALE = 0.35;
const MAX_SCALE = 2.2;

// A node's accent: rarity colour for card/gear/pack; emerald for buffs.
const nodeColor = (n) => (n.type === "buff" ? "#34d399" : (RARITY_COLORS[n.rarity] || "#9ca3af"));

// Lay the selected branch out as a COLS×ROWS grid + diagonal links (down-left /
// down / down-right) so the player can branch outward from the top-centre entry.
function useLayout(branch) {
  return useMemo(() => {
    const cols = branch?.cols || 3;
    const rows = branch?.rows || 10;
    const byId = {};
    (branch?.nodes || []).forEach((n) => { byId[n.id] = n; });
    const pos = {};
    (branch?.nodes || []).forEach((n) => {
      pos[n.id] = {
        x: PAD_X + n.col * COL_W + COL_W / 2,
        y: PAD_Y + n.row * ROW_H + ROW_H / 2,
        node: n,
      };
    });
    const links = [];
    (branch?.nodes || []).forEach((n) => {
      [n.col - 1, n.col, n.col + 1].forEach((cc) => {
        if (cc < 0 || cc >= cols) return;
        const childId = `${n.row + 1}:${cc}`;
        if (byId[childId]) links.push({ from: n.id, to: childId });
      });
    });
    const width = PAD_X * 2 + cols * COL_W;
    const height = PAD_Y * 2 + rows * ROW_H;
    return { pos, links, width, height };
  }, [branch]);
}

function NodeOrb({ p, color, onUnlock, unlocking, clickedId, setClickedId, branchKey }) {
  const n = p.node;
  const state = n.unlocked ? "unlocked" : n.available ? (n.affordable ? "ready" : "poor") : "locked";
  const clickable = state === "ready";
  const Icon = TYPE_ICON[n.type] || Gem;
  const size = 54;
  const surface = state === "unlocked" ? `${color}26` : "rgba(10,12,20,0.72)";
  const border = state === "unlocked" ? color : state === "locked" ? "rgba(255,255,255,0.08)" : `${color}66`;

  const handle = () => {
    if (!clickable || unlocking) return;
    setClickedId(n.id);
    onUnlock({ branch: branchKey, cell: n.id });
  };

  return (
    <div className="absolute group" style={{ left: p.x, top: p.y, transform: "translate(-50%, -50%)", zIndex: 5 }}>
      <div className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-3 w-52 rounded-xl border bg-[#0A0A0F]/95 backdrop-blur-md p-3 opacity-0 translate-y-1 transition-all duration-200 group-hover:opacity-100 group-hover:translate-y-0 z-50"
        style={{ borderColor: `${color}66`, boxShadow: `0 0 24px ${color}33` }}>
        <div className="font-mono text-[8px] uppercase tracking-[0.25em] mb-1" style={{ color }}>
          {TYPE_LABEL[n.type] || "Reward"}{n.rarity ? ` · ${n.rarity}` : ""}
        </div>
        <div className="text-white/75 text-[12px] leading-snug">{n.desc}</div>
        <div className="mt-2 flex items-center justify-between">
          <span className="font-mono text-[9px] text-white/40 uppercase">Row {n.row + 1}</span>
          {n.unlocked
            ? <span className="inline-flex items-center gap-1 text-emerald-400 font-mono text-[10px]"><Check className="h-3 w-3" /> Owned</span>
            : <span className="inline-flex items-center gap-1 font-mono text-[11px]" style={{ color: state === "ready" ? "#fde047" : "#ffffff66" }}>
                {state === "locked" && <Lock className="h-3 w-3" />}<Star className="h-3 w-3" /> {n.cost} SP
              </span>}
        </div>
      </div>

      <button type="button" data-testid={`tree-node-${n.id}`} onClick={handle} onPointerDown={(e) => e.stopPropagation()}
        className={`relative grid place-items-center rounded-2xl border backdrop-blur-md transition-transform duration-200 ${clickable ? "cursor-pointer hover:-translate-y-1" : "cursor-default"} ${state === "ready" ? "skill-node-ready" : ""} ${clickedId === n.id && unlocking ? "skill-node-unlocking" : ""}`}
        style={{
          "--branch-color": color, width: size, height: size, background: surface, borderColor: border,
          opacity: state === "locked" ? 0.5 : 1,
          boxShadow: state === "unlocked" ? `0 0 20px ${color}55, inset 0 0 14px ${color}33` : clickable ? `0 0 16px ${color}40` : "none",
        }}>
        <span className="absolute inset-0 rounded-2xl pointer-events-none" style={{ background: `linear-gradient(135deg, ${color}26, transparent 55%)` }} />
        <Icon className="relative z-10 h-5 w-5" style={{ color: n.unlocked || clickable ? color : "#ffffff66" }} />
        {!n.unlocked && (
          <span className="absolute -bottom-1 translate-y-full mt-1 font-mono text-[9px] flex items-center gap-0.5" style={{ color: state === "ready" ? "#fde047" : "#ffffff55" }}>
            <Star className="h-2.5 w-2.5" />{n.cost}
          </span>
        )}
        {n.unlocked && <Check className="absolute -bottom-1 translate-y-full h-3 w-3 text-emerald-400" />}
      </button>
    </div>
  );
}

// A node is "revealed" once it's unlocked or currently open to unlock — anything
// still fully locked (its unlocking predecessor hasn't been taken yet) stays
// completely hidden, so the tree only shows the path you've actually carved.
const isRevealed = (n) => n.unlocked || n.available;

function computeBBox(pos, revealedIds) {
  const pts = Object.values(pos).filter((p) => revealedIds.has(p.node.id));
  const minSpanW = COL_W * 3;
  const minSpanH = ROW_H * 2.6;
  if (!pts.length) return { minX: -minSpanW / 2, maxX: minSpanW / 2, minY: 0, maxY: minSpanH };
  const xs = pts.map((p) => p.x), ys = pts.map((p) => p.y);
  let minX = Math.min(...xs) - COL_W / 2, maxX = Math.max(...xs) + COL_W / 2;
  let minY = Math.max(0, Math.min(...ys) - ROW_H / 2), maxY = Math.max(...ys) + ROW_H / 2;
  if (maxX - minX < minSpanW) { const mid = (minX + maxX) / 2; minX = mid - minSpanW / 2; maxX = mid + minSpanW / 2; }
  if (maxY - minY < minSpanH) maxY = minY + minSpanH;
  return { minX, maxX, minY, maxY };
}

export function SkillTreeMap({ branches, onUnlock, unlocking }) {
  const [activeKey, setActiveKey] = useState((branches?.[0]?.key) || "");
  const branch = useMemo(() => (branches || []).find((b) => b.key === activeKey) || branches?.[0], [branches, activeKey]);
  const { pos, links, width, height } = useLayout(branch);
  const [clickedId, setClickedId] = useState(null);
  const viewportRef = useRef(null);
  const color = branch?.color || "#38bdf8";

  const revealedIds = useMemo(() => new Set(Object.values(pos).filter((p) => isRevealed(p.node)).map((p) => p.node.id)), [pos]);
  const revealedCount = revealedIds.size;
  const bbox = useMemo(() => computeBBox(pos, revealedIds), [pos, revealedIds]);

  // view = the pan/zoom transform applied to the content layer (top-left origin).
  const [view, setView] = useState({ x: 0, y: 0, scale: 1 });
  const [animateView, setAnimateView] = useState(false);
  const userAdjustedRef = useRef(false);
  const dragRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);

  const fitTo = useCallback((box, animate) => {
    const el = viewportRef.current;
    if (!el) return;
    const cw = el.clientWidth, ch = el.clientHeight;
    if (!cw || !ch) return;
    const bw = Math.max(1, box.maxX - box.minX);
    const bh = Math.max(1, box.maxY - box.minY);
    const scale = Math.max(MIN_SCALE, Math.min(MAX_SCALE, Math.min(cw / bw, ch / bh) * 0.92));
    const cx = (box.minX + box.maxX) / 2;
    setAnimateView(animate);
    setView({ x: cw / 2 - cx * scale, y: 36 - box.minY * scale, scale });
  }, []);

  // Re-fit only for an actual branch change, not every refreshed bounding box.
  const fittedBranch = useRef(null);
  useLayoutEffect(() => {
    if (fittedBranch.current === activeKey) return;
    fittedBranch.current = activeKey;
    userAdjustedRef.current = false;
    fitTo(bbox, false);
  }, [activeKey, bbox, fitTo]);

  // Auto-recenter as new nodes get revealed (a successful unlock) — but only if
  // the player hasn't manually taken control of the camera since the last fit.
  const prevCountRef = useRef(revealedCount);
  useEffect(() => {
    if (revealedCount !== prevCountRef.current) {
      prevCountRef.current = revealedCount;
      if (!userAdjustedRef.current) fitTo(bbox, true);
    }
  }, [revealedCount, bbox, fitTo]);

  useLayoutEffect(() => {
    const el = viewportRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => { if (!userAdjustedRef.current) fitTo(bbox, false); });
    ro.observe(el);
    return () => ro.disconnect();
  }, [bbox, fitTo]);

  const zoomBy = useCallback((factor) => {
    const el = viewportRef.current;
    if (!el) return;
    userAdjustedRef.current = true;
    setAnimateView(true);
    setView((v) => {
      const cw = el.clientWidth, ch = el.clientHeight;
      const newScale = Math.max(MIN_SCALE, Math.min(MAX_SCALE, v.scale * factor));
      const cx = (cw / 2 - v.x) / v.scale, cy = (ch / 2 - v.y) / v.scale;
      return { x: cw / 2 - cx * newScale, y: ch / 2 - cy * newScale, scale: newScale };
    });
  }, []);

  // Wheel-to-zoom (centred on the cursor), attached natively so preventDefault works.
  useEffect(() => {
    const el = viewportRef.current;
    if (!el) return;
    const onWheel = (e) => {
      e.preventDefault();
      userAdjustedRef.current = true;
      setAnimateView(false);
      const rect = el.getBoundingClientRect();
      const mx = e.clientX - rect.left, my = e.clientY - rect.top;
      setView((v) => {
        const factor = e.deltaY < 0 ? 1.12 : 0.89;
        const newScale = Math.max(MIN_SCALE, Math.min(MAX_SCALE, v.scale * factor));
        const cx = (mx - v.x) / v.scale, cy = (my - v.y) / v.scale;
        return { x: mx - cx * newScale, y: my - cy * newScale, scale: newScale };
      });
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, []);

  const onPointerDown = (e) => {
    if (e.target.closest("button")) return;
    dragRef.current = { startX: e.clientX, startY: e.clientY, viewX: view.x, viewY: view.y, moved: false };
    setIsDragging(true);
    setAnimateView(false);
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };
  const onPointerMove = (e) => {
    const d = dragRef.current;
    if (!d) return;
    const dx = e.clientX - d.startX, dy = e.clientY - d.startY;
    if (Math.abs(dx) > 3 || Math.abs(dy) > 3) d.moved = true;
    setView((v) => ({ ...v, x: d.viewX + dx, y: d.viewY + dy }));
  };
  const endDrag = () => {
    if (dragRef.current?.moved) userAdjustedRef.current = true;
    dragRef.current = null;
    setIsDragging(false);
  };

  if (!branch) return null;
  const unlockedCount = branch.nodes.filter((n) => n.unlocked).length;

  return (
    <div data-testid="skill-tree">
      <div className="mb-3 flex items-center gap-2 text-white/55 text-xs">
        <Sparkles className="h-4 w-4 text-yellow-300 shrink-0" />
        Start at the top and branch <span className="text-amber-300 font-semibold">outward</span> — collecting a node reveals what's below it. Drag to pan, scroll or use the controls to zoom.
      </div>

      {/* Category selector */}
      <div className="mb-4 flex flex-wrap gap-2" data-testid="tree-branch-tabs">
        {(branches || []).map((b) => {
          const active = b.key === activeKey;
          const owned = b.nodes.filter((n) => n.unlocked).length;
          return (
            <button key={b.key} type="button" data-testid={`tree-branch-tab-${b.key}`}
              onClick={() => setActiveKey(b.key)}
              className={`rounded-xl px-3 py-1.5 text-xs font-display tracking-wide transition-all border ${active ? "" : "opacity-60 hover:opacity-100"}`}
              style={{ color: b.color, background: active ? `${b.color}1f` : "rgba(255,255,255,0.03)", borderColor: active ? `${b.color}80` : "rgba(255,255,255,0.1)" }}>
              {b.label} <span className="text-white/40 font-mono">{owned}/{b.nodes.length}</span>
            </button>
          );
        })}
      </div>

      <div ref={viewportRef} onPointerDown={onPointerDown} onPointerMove={onPointerMove} onPointerUp={endDrag} onPointerLeave={endDrag}
        className={`relative w-full h-[600px] overflow-hidden rounded-2xl border border-white/10 bg-[#05050a] select-none ${isDragging ? "cursor-grabbing" : "cursor-grab"}`}
        style={{ backgroundImage: `radial-gradient(circle at 50% 12%, ${color}10, transparent 60%)`, touchAction: "none" }}>

        {/* Zoom / pan controls */}
        <div className="absolute right-3 top-3 z-20 flex flex-col gap-1.5">
          <button type="button" data-testid="tree-zoom-in" onClick={() => zoomBy(1.25)}
            className="h-8 w-8 grid place-items-center rounded-lg border border-white/15 bg-black/60 backdrop-blur-md text-white/70 hover:text-white hover:border-white/30 transition-colors">
            <Plus className="h-4 w-4" />
          </button>
          <button type="button" data-testid="tree-zoom-out" onClick={() => zoomBy(0.8)}
            className="h-8 w-8 grid place-items-center rounded-lg border border-white/15 bg-black/60 backdrop-blur-md text-white/70 hover:text-white hover:border-white/30 transition-colors">
            <Minus className="h-4 w-4" />
          </button>
          <button type="button" data-testid="tree-zoom-reset" onClick={() => { userAdjustedRef.current = false; fitTo(bbox, true); }}
            className="h-8 w-8 grid place-items-center rounded-lg border border-white/15 bg-black/60 backdrop-blur-md text-white/70 hover:text-white hover:border-white/30 transition-colors">
            <Scan className="h-4 w-4" />
          </button>
        </div>

        <div className="absolute left-1/2 -translate-x-1/2 text-center z-10 pointer-events-none" style={{ top: 8 }}>
          <div className="font-display text-base tracking-wide" style={{ color }} data-testid={`branch-${branch.key}`}>{branch.label}</div>
          <div className="text-white/40 text-[10px]">{unlockedCount}/{branch.nodes.length} collected</div>
        </div>

        <div className="absolute" style={{
          width, height, left: 0, top: 0,
          transform: `translate(${view.x}px, ${view.y}px) scale(${view.scale})`, transformOrigin: "0 0",
          transition: animateView ? "transform 0.5s cubic-bezier(0.22, 1, 0.36, 1)" : "none",
        }}>
          <svg className="absolute inset-0 pointer-events-none" width={width} height={height} style={{ overflow: "visible" }}>
            {links.map((l, i) => {
              const a = pos[l.from], b = pos[l.to];
              if (!a || !b || !revealedIds.has(l.from) || !revealedIds.has(l.to)) return null;
              const active = a.node.unlocked && b.node.unlocked;
              const reachable = a.node.unlocked && b.node.available;
              return (
                <line key={`${a.node.id}:${b.node.id}`} x1={a.x} y1={a.y} x2={b.x} y2={b.y}
                  stroke={active || reachable ? color : "rgba(255,255,255,0.10)"}
                  strokeWidth={active ? 3.5 : reachable ? 2.5 : 1.5}
                  strokeDasharray={active ? "12 12" : reachable ? "6 8" : "3 7"}
                  style={active ? { filter: `drop-shadow(0 0 6px ${color})`, animation: "flowEnergy 0.9s linear infinite" } : {}}
                  strokeLinecap="round" />
              );
            })}
          </svg>

          {Object.values(pos).filter((p) => revealedIds.has(p.node.id)).map((p) => (
            <NodeOrb key={p.node.id} p={p} color={nodeColor(p.node)} onUnlock={onUnlock} unlocking={unlocking}
              clickedId={clickedId} setClickedId={setClickedId} branchKey={branch.key} />
          ))}
        </div>
      </div>
    </div>
  );
}
